import {
  Message,
  MessageItem,
  RelationshipLetterMessage,
  RelationshipMonologueMessage,
  RelationshipEventMessage,
  RelationshipRomanticMomentMessage,
  RelationshipTidalHeartsVideoMessage,
  RelationshipExclusiveMemoryMessage,
  RelationshipDreamMessage,
  RelationshipMysteryRewardsMessage,
  RelationshipGemRefundMessage,
  RelationshipLotteryRewardMessage,
} from '@/types/message'
import { i18nKey } from '@/lib/i18n-key'

export type AppOnlyImagePlaceholderMessage = Message & {
  app_only_image_placeholder?: true
}

export const APP_ONLY_IMAGE_PLACEHOLDER = i18nKey(
  '[Image · Available in app only]'
)

export function localizeAppOnlyImagePlaceholderContent(
  content: string,
  t: (key: string) => string
): string {
  if (!content.includes(APP_ONLY_IMAGE_PLACEHOLDER)) return content
  return content
    .split(APP_ONLY_IMAGE_PLACEHOLDER)
    .join(t(APP_ONLY_IMAGE_PLACEHOLDER))
}

// 所有亲密度消息类型常量
export const RELATIONSHIP_MESSAGE_TYPES = [
  'relationship_letter_with_image',
  'relationship_monologue_slot',
  'relationship_event_slot',
  'relationship_romantic_moment',
  'tidal_hearts_video',
  'relationship_memory_album',
  'relationship_dream_slot',
  'relationship_gem_refund',
  'lottery_reward',
] as const

// 检查是否为任意亲密度消息
export function isRelationshipMessage(m?: MessageItem): boolean {
  if (!m || !('type' in m)) return false
  return RELATIONSHIP_MESSAGE_TYPES.includes((m as any).type)
}

export function isAppOnlyImagePlaceholderMessage(m?: MessageItem): boolean {
  if (!m || !('content' in m) || !('content_type' in m)) return false

  const message = m as MessageItem & {
    app_only_image_placeholder?: boolean
    content?: unknown
    content_type?: unknown
  }

  return message.app_only_image_placeholder === true
}

// 检查是否为神秘包装的消息
export function isMysteryWrappedMessage(m?: MessageItem): boolean {
  if (!m) return false
  return (m as any).is_from_surprise_box === true
}

// 检查是否为关系信件消息
export function isRelationshipLetterMessage(
  m?: MessageItem
): m is RelationshipLetterMessage {
  if (!m) return false
  return (
    (m as RelationshipLetterMessage).type === 'relationship_letter_with_image'
  )
}

// 检查是否为关系内心独白消息
export function isRelationshipMonologueMessage(
  m?: MessageItem
): m is RelationshipMonologueMessage {
  if (!m) return false
  return (
    (m as RelationshipMonologueMessage).type === 'relationship_monologue_slot'
  )
}

// 检查是否为关系过往事件消息
export function isRelationshipEventMessage(
  m?: MessageItem
): m is RelationshipEventMessage {
  if (!m) return false
  return (m as RelationshipEventMessage).type === 'relationship_event_slot'
}

// 检查是否为关系浪漫时刻消息
export function isRelationshipRomanticMomentMessage(
  m?: MessageItem
): m is RelationshipRomanticMomentMessage {
  if (!m) return false
  return (
    (m as RelationshipRomanticMomentMessage).type ===
    'relationship_romantic_moment'
  )
}

export function isRelationshipTidalHeartsVideoMessage(
  m?: MessageItem
): m is RelationshipTidalHeartsVideoMessage {
  if (!m) return false
  return (
    (m as RelationshipTidalHeartsVideoMessage).type === 'tidal_hearts_video'
  )
}

// 检查是否为关系专属回忆消息
export function isRelationshipExclusiveMemoryMessage(
  m?: MessageItem
): m is RelationshipExclusiveMemoryMessage {
  if (!m) return false
  return (
    (m as RelationshipExclusiveMemoryMessage).type ===
    'relationship_memory_album'
  )
}

// 检查是否为抽奖奖励消息
export function isRelationshipLotteryRewardMessage(
  m?: MessageItem
): m is RelationshipLotteryRewardMessage {
  if (!m) return false
  return (m as RelationshipLotteryRewardMessage).type === 'lottery_reward'
}

// 信件弹窗内容接口
export interface LetterContent {
  imageUrl: string
  content: string
}

// 处理关系信件消息，转换为弹窗友好的内容格式
export function processLetterMessage(
  message:
    | RelationshipLetterMessage
    | RelationshipExclusiveMemoryMessage
    | null,
  messageType: 'letter' | 'exclusive'
): LetterContent[] {
  // letter 和 exclusive 类型的消息数据都通过接口动态获取，此处返回空数组
  return []
}

// 内心独白和过往事件弹窗内容接口
export interface MonologueAndEventsContent {
  content: string
}

// 处理内心独白和过往事件消息，转换为弹窗友好的内容格式（已废弃，数据现通过接口动态获取）
export function processMonologueAndEventsMessage(
  message: RelationshipMonologueMessage | null
): MonologueAndEventsContent {
  return {
    content: '',
  }
}
